<app-modal
  [title]="'admin.customers.edit.logo.title' | transloco"
  [subtitle]="'admin.customers.edit.logo.subtitle' | transloco"
  [errorMessage]="errorMessage()"
  [isSubmitting]="isLoading()"
  [isDisabled]="!canSubmit()"
  [confirmLabel]="'admin.customers.edit.logo.confirmLabel' | transloco"
  [submittingLabel]="'shared.actions.saving' | transloco"
  (confirm)="onSubmit()"
  (cancelled)="cancel()"
  icon="bi bi-image"
>
  <div class="d-flex flex-column gap-4">
    <!-- Nome da empresa -->
    <section class="d-flex flex-column gap-2">
      <div>
        <h6 class="mb-1 font-heading text-primary-brand">
          {{ 'admin.customers.edit.logo.companyNameTitle' | transloco }}
        </h6>

        <p class="mb-0 small text-secondary-brand">{{ 'admin.customers.edit.logo.companyNameHelp' | transloco }}</p>
      </div>

      <div class="input-group">
        <span class="input-group-text">
          <i class="bi bi-building"></i>
        </span>

        <input
          id="companyName"
          type="text"
          class="form-control"
          maxlength="150"
          [placeholder]="'admin.customers.edit.logo.companyNamePlaceholder' | transloco"
          [value]="customerName()"
          [disabled]="true"
          (input)="onCompanyNameChange($event)"
        />
      </div>

      @if (!customerName().trim()) {
        <small class="text-danger"> {{ 'admin.customers.edit.logo.companyNameRequired' | transloco }} </small>
      }
    </section>

    <hr class="my-0" />

    <!-- Upload -->
    <section class="d-flex flex-column gap-3">
      <div>
        <h6 class="mb-1 font-heading text-primary-brand">
          {{ 'admin.customers.edit.logo.imageFileTitle' | transloco }}
        </h6>

        <p class="mb-0 small text-secondary-brand">{{ 'admin.customers.edit.logo.imageFileHelp' | transloco }}</p>
      </div>

      <input
        #fileInput
        type="file"
        hidden
        accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
        (change)="onFileSelected($event)"
      />

      <!-- Área compacta de upload -->
      <div
        class="logo-drop-zone d-flex align-items-center gap-3 p-3 border-default rounded-3 bg-color-surface-light"
        [class.logo-drop-zone-active]="isDragging()"
        [class.logo-drop-zone-disabled]="isLoading()"
        role="button"
        tabindex="0"
        (click)="openFilePicker()"
        (keydown.enter)="openFilePicker()"
        (keydown.space)="openFilePicker()"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
      >
        <span
          class="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-circle bg-color-surface-lighter text-success-brand"
          style="width: 42px; height: 42px"
        >
          <i class="bi bi-cloud-arrow-up fs-5"></i>
        </span>

        <span class="d-flex flex-column flex-grow-1">
          <strong class="font-heading text-primary-brand">
            {{ 'admin.customers.edit.logo.dropTitle' | transloco }}
          </strong>

          <small class="text-secondary-brand"> {{ 'admin.customers.edit.logo.dropHelp' | transloco }} </small>
        </span>

        <span class="btn btn-sm btn-outline-primary flex-shrink-0">
          {{ 'admin.customers.edit.logo.chooseFile' | transloco }}
        </span>
      </div>

      <!-- Arquivo selecionado -->
      @if (selectedFile(); as file) {
        <div class="d-flex align-items-center gap-3 p-3 border-default rounded-3 bg-color-surface-light">
          <span
            class="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-3 bg-color-surface-lighter text-success-brand"
            style="width: 40px; height: 40px"
          >
            <i class="bi bi-file-earmark-image"></i>
          </span>

          <span class="d-flex flex-column flex-grow-1 overflow-hidden">
            <strong class="text-primary-brand text-truncate">
              {{ file.name }}
            </strong>

            <small class="text-secondary-brand">
              {{ selectedFileSize() }}
            </small>
          </span>

          <button
            type="button"
            class="btn btn-sm btn-outline-secondary flex-shrink-0"
            [disabled]="isLoading()"
            (click)="restoreLogo(); $event.stopPropagation()"
          >
            <i class="bi bi-x-lg me-1"></i>
            {{ 'admin.customers.edit.logo.discardFile' | transloco }}
          </button>
        </div>
      }

      <!-- Ações -->
      <div class="d-flex flex-wrap gap-2">
        @if (previewUrl() && !selectedFile() && !removeRequested()) {
          <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="isLoading()" (click)="removeLogo()">
            <i class="bi bi-trash me-2"></i>
            {{ 'admin.customers.edit.logo.removeImage' | transloco }}
          </button>
        }

        @if (removeRequested()) {
          <button type="button" class="btn btn-sm btn-outline-info" [disabled]="isLoading()" (click)="restoreLogo()">
            <i class="bi bi-arrow-counterclockwise me-2"></i>

            {{ 'admin.customers.edit.logo.undoRemoval' | transloco }}
          </button>
        }
      </div>

      @if (removeRequested()) {
        <div class="alert alert-warning mb-0" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>

          {{ 'admin.customers.edit.logo.removalWarning' | transloco }}
        </div>
      }
    </section>

    <hr class="my-0" />

    <!-- Pré-visualização -->
    <section class="d-flex flex-column gap-3">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div>
          <h6 class="mb-1 font-heading text-primary-brand">
            {{ 'admin.customers.edit.logo.previewTitle' | transloco }}
          </h6>

          <p class="mb-0 small text-secondary-brand">
            {{ 'admin.customers.edit.logo.previewHelp' | transloco }}
          </p>
        </div>

        <span class="badge bg-secondary"> {{ 'admin.customers.edit.logo.previewBadge' | transloco }} </span>
      </div>

      <div
        class="d-flex align-items-center justify-content-center p-4 border-default rounded-3 bg-color-surface-light"
        style="min-height: 140px"
      >
        <div class="d-inline-flex align-items-center gap-3 mw-100">
          @if (previewUrl() && !removeRequested()) {
            <span
              class="d-inline-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden border-default rounded-3 bg-color-default"
              style="width: 72px; height: 72px"
            >
              <img
                [src]="previewUrl()"
                [alt]="'admin.customers.edit.logo.logoAlt' | transloco: { name: displayName() }"
                style="display: block; width: 100%; height: 100%; object-fit: cover; object-position: center"
                (error)="onPreviewError()"
              />
            </span>
          }

          <span class="font-heading text-primary-brand fw-bold fs-5 text-truncate">
            {{ displayName() }}
          </span>
        </div>
      </div>

      <small class="text-secondary-brand"> {{ 'admin.customers.edit.logo.noImageHelp' | transloco }} </small>
    </section>
  </div>
</app-modal>
